import { Constructor } from '@angular/cdk/table';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import {
  filter,
  finalize,
  first,
  map,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { FORM_PATHS } from 'src/app/article-content/components/article-content-form/constants/form-paths';
import { IContentList } from 'src/app/core/models/i-content-list';
import { WithWebsitesFormSectionType } from './with-websites-form-section';
import { DeliveryTargetsService } from 'src/app/content-list-management/shared/services/cmsapi/delivery-targets.service';
import { ContentListService } from 'src/app/content-list-management/shared/services/cmsapi/content-list.service';
import { ITopic } from 'src/app/core/models/i-topic';
import { ITag } from 'src/app/core/models/i-tag';
import { IContent } from 'src/app/core/models/i-content';

type WithContentListFromWebsiteType = {
  _initContentListTracking(): void;
  _initContentListsForNewContent(deliveryTargetCodes: string[]): void;
  _handleEditionAdded(deliveryTargetCode: string);
  _handleEditionRemoved(deliveryTargetCode: string);
};

type WithContentListFromWebsiteRequirementsBase = {
  form: FormGroup;
  FORM_PATHS: { WEBSITES: () => string };
  destroy$: Subject<boolean>;
  deliveryTargetsService: DeliveryTargetsService;
  contentListService: ContentListService;
  formBuilder: FormBuilder;
} & WithWebsitesFormSectionType;

export function WithContentListFromWebsite<
  TBase extends Constructor<WithContentListFromWebsiteRequirementsBase>
>(
  baseClass: TBase
): {
  new (...args: any[]): WithContentListFromWebsiteType;
  prototype: WithContentListFromWebsiteType;
} & TBase {
  return class ContentListFromWebsite extends baseClass {
    constructor(...args: any[]) {
      super(...args);
    }

    _initContentListsForNewContent(deliveryTargetCodes: string[]) {
      if (deliveryTargetCodes) {
        this.contentListService
          .getSuggested$(null, deliveryTargetCodes)
          .subscribe({
            next: (newLists: IContentList[]) => {
              let contentListControl = this.form.get(
                FORM_PATHS.CONTENT_LISTS()
              ) as FormArray;
              if (
                !this.form.get(FORM_PATHS.IS_SPONSORED()) ||
                !this.form.get(FORM_PATHS.IS_SPONSORED()).value
              ) {
                this._populateContentListFromWebsite(
                  newLists,
                  contentListControl
                );
              }
            },
          });
      }
    }

    _initContentListTracking() {
      this.isWebsiteInitComplete$
        .pipe(
          takeUntil(this.destroy$),
          filter((isComplete) => isComplete),
          switchMap(() => {
            return this.form.get(FORM_PATHS.TOPICS()).valueChanges.pipe(
              filter(() => this.shouldListenToWebsiteForm),
              switchMap((topics: ITopic[]) => {
                if (this._buildDeliveryTargetCodes().length > 0) {
                  return this.contentListService.getSuggested$(
                    null,
                    this._buildDeliveryTargetCodes(),
                    topics.map((x) => x.id.toString()),
                    this.form
                      .get(FORM_PATHS.TAGS())
                      .value.map((tags) => tags.id.toString())
                    // TODO: Add topic and tags ids here
                  );
                }
                return of(null);
              })
            );
          })
        )
        .subscribe({
          next: (newLists: IContentList[]) => {
            if (
              !this.form.get(FORM_PATHS.IS_SPONSORED()) ||
              !this.form.get(FORM_PATHS.IS_SPONSORED()).value
            ) {
              let contentListControl = this.form.get(
                FORM_PATHS.CONTENT_LISTS()
              ) as FormArray;
              this._populateContentListFromWebsite(
                newLists,
                contentListControl
              );
            }
          },
        });

      this.isWebsiteInitComplete$
        .pipe(
          takeUntil(this.destroy$),
          filter((isComplete) => isComplete),
          switchMap(() => {
            return this.form.get(FORM_PATHS.TAGS()).valueChanges.pipe(
              filter(() => this.shouldListenToWebsiteForm),
              switchMap((tags: ITag[]) => {
                if (this._buildDeliveryTargetCodes().length > 0) {
                  return this.contentListService.getSuggested$(
                    null,
                    this._buildDeliveryTargetCodes(),
                    this.form
                      .get(FORM_PATHS.TOPICS())
                      .value.map((topics) => topics.id.toString()),
                    tags.map((x) => x.id)
                    // TODO: Add topic and tags ids here
                  );
                }
                return of(null);
              })
            );
          })
        )
        .subscribe({
          next: (newLists: IContentList[]) => {
            if (
              !this.form.get(FORM_PATHS.IS_SPONSORED()) ||
              !this.form.get(FORM_PATHS.IS_SPONSORED()).value
            ) {
              let contentListControl = this.form.get(
                FORM_PATHS.CONTENT_LISTS()
              ) as FormArray;
              this._populateContentListFromWebsite(
                newLists,
                contentListControl
              );
            }
          },
        });
    }

    _handleEditionAdded(deliveryTargetCode: string) {
      if (this.shouldListenToWebsiteForm) {
        let suggestedForDeliveryTarget$ = this.contentListService.getSuggested$(
          null,
          [deliveryTargetCode],
          null,
          null
        );
        let suggestedForTopicsAndTags$ = this.contentListService.getSuggested$(
          null,
          this._buildDeliveryTargetCodes(),
          this.form
            .get(FORM_PATHS.TOPICS())
            .value.map((topics) => topics.id.toString()),
          this.form
            .get(FORM_PATHS.TAGS())
            .value.map((tags) => tags.id.toString())
        );

        forkJoin([suggestedForDeliveryTarget$, suggestedForTopicsAndTags$])
          .pipe(
            map(([newForEdition, newForTopics]) => {
              return [...newForEdition, ...newForTopics];
            })
          )
          .subscribe((newLists: IContentList[]) => {
            if (
              !this.form.get(FORM_PATHS.IS_SPONSORED()) ||
              !this.form.get(FORM_PATHS.IS_SPONSORED()).value
            ) {
              let contentListControl = this.form.get(
                FORM_PATHS.CONTENT_LISTS()
              ) as FormArray;
              this._removeUnselectedWebsitesContentLists(contentListControl);
              this._populateContentListFromWebsite(
                newLists,
                contentListControl
              );
            }
          });
      }
    }

    _handleEditionRemoved(deliveryTargetCode: string) {
      let contentListControl = this.form.get(
        FORM_PATHS.CONTENT_LISTS()
      ) as FormArray;
      this._removeUnselectedWebsitesContentLists(contentListControl);
    }

    private _removeUnselectedWebsitesContentLists(
      contentListControl: FormArray
    ) {
      const selectedWebsites = this._buildDeliveryTargetCodes();
      contentListControl.value.forEach((contentList: IContentList) => {
        const isAllowed = contentList.deliveryTargetCodes.some((dtc) =>
          selectedWebsites.includes(dtc)
        );

        if (!isAllowed) {
          let contentListToRemove = contentListControl.controls.findIndex(
            (remove) => {
              return remove.value.id === contentList.id;
            }
          );
          contentListControl.removeAt(contentListToRemove);
        }
      });
    }

    private _populateContentListFromWebsite(
      newLists: IContentList[],
      contentListControl: FormArray
    ) {
      newLists?.forEach((listToAdd) => {
        let selectedContentLists: IContentList[] = this.form.get(
          FORM_PATHS.CONTENT_LISTS()
        ).value;

        const isInSelectedList = selectedContentLists
          .map((scl) => scl.id)
          .includes(listToAdd.id);
        if (!isInSelectedList) {
          contentListControl.push(new FormControl(listToAdd));
        }
      });
    }
  };
}
